/* eslint-disable prettier/prettier */
import React, { useState } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  Box,
  Text,
  SimpleGrid,
  Flex,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Badge,
  Spinner,
  ButtonGroup,
  Checkbox,
  HStack,
  VStack,
} from '@chakra-ui/react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell,
  LineChart,
  Line,
} from 'recharts';
import { useQuery } from '@tanstack/react-query';
import moment from 'moment';
import { ITaxFaturamentoSNItem, useTaxAnalysis } from '../../../../../hooks/useTaxAnalysis';
import {
  ITaxAnalysisSNHistory,
  formatarEmReais,
  formatarPercentual,
  calculateTaxPercentage,
} from '../../../../../types/taxAnalysis';

// Paleta de cores inspirada no design Apple
const APPLE_COLORS = {
  FATURAMENTO: '#0A84FF', // Azul iOS
  SIMPLES_NACIONAL: '#30D158', // Verde Apple
};

// Função para formatar valores no eixo Y do gráfico
const formatarValorEixoY = (valor: number): string => {
  if (valor >= 1000000) {
    return `${(valor / 1000000).toFixed(1)}M`;
  }
  if (valor >= 1000) {
    return `${(valor / 1000).toFixed(0)}K`;
  }
  return valor.toFixed(0);
};

interface TaxDetailModalSNProps {
  isOpen: boolean;
  onClose: () => void;
  companyData: ITaxFaturamentoSNItem | null;
  dateStart?: string;
  dateEnd?: string;
}

const formatPeriodo = (dateStart?: string, dateEnd?: string) => {
  if (!dateStart || !dateEnd) return null;
  const start = moment(dateStart).format('DD/MM/YYYY');
  const end = moment(dateEnd).format('DD/MM/YYYY');
  return `${start} a ${end}`;
};

export function TaxDetailModalSN({
  isOpen,
  onClose,
  companyData,
  dateStart,
  dateEnd,
}: TaxDetailModalSNProps) {
  const { getTaxAnalysisSNHistoryByPeriod } = useTaxAnalysis();
  const [chartMode, setChartMode] = useState<'line' | 'bar'>('line');
  const [showFaturamento, setShowFaturamento] = useState(true);
  const [showSimplesNacional, setShowSimplesNacional] = useState(true);

  // Query para buscar histórico mensal de SN
  const { data: snHistoryData, isFetching: isLoadingSNHistory } = useQuery<ITaxAnalysisSNHistory[]>(
    {
      queryKey: [
        'TaxAnalysisSNHistoryByPeriod',
        { companyId: companyData?.TbEmpresa?.external_id, dateStart, dateEnd },
      ],
      queryFn: getTaxAnalysisSNHistoryByPeriod,
      enabled: isOpen && !!companyData?.TbEmpresa?.external_id && !!dateStart && !!dateEnd,
    },
  );

  if (!companyData) return null;

  const company = companyData.TbEmpresa;
  const totalFaturamento = companyData.total_faturamento || 0;
  const vlSimplesNacional = companyData.vl_simples_nacional || 0;
  const vlSaida = companyData.vl_saida || 0;
  const vlServico = companyData.vl_servico || 0;
  const vlOutros = companyData.vl_outros || 0;
  const iss = companyData.iss || 0;
  const issRetido = companyData.iss_retido || 0;

  const taxData = {
    razao_social: company.razao_social,
    cnpj: company.cnpj,
    total_faturamento: totalFaturamento,
    vl_simples_nacional: vlSimplesNacional,
    simples_nacional_percentual: calculateTaxPercentage(vlSimplesNacional, totalFaturamento),
    vl_saida: vlSaida,
    vl_servico: vlServico,
    vl_outros: vlOutros,
    iss,
    iss_percentual: calculateTaxPercentage(iss, totalFaturamento),
    iss_retido: issRetido,
    iss_retido_percentual: calculateTaxPercentage(issRetido, totalFaturamento),
  };

  // Dados para o grafico de barras comparativo com cores Apple
  const barData = [
    {
      name: 'Faturamento',
      valor: taxData.total_faturamento,
      fill: APPLE_COLORS.FATURAMENTO,
    },
    {
      name: 'Simples Nacional',
      valor: taxData.vl_simples_nacional,
      fill: APPLE_COLORS.SIMPLES_NACIONAL,
    },
  ];

  // Tooltip customizado com valor + porcentagem (estilo Apple glassmorphism)
  const CustomBarTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      const percentual =
        data.name === 'Faturamento' ? 100 : (data.valor / taxData.total_faturamento) * 100;

      return (
        <Box
          bg="rgba(255, 255, 255, 0.85)"
          backdropFilter="blur(12px)"
          p={4}
          borderRadius="12px"
          boxShadow="0 4px 24px rgba(0, 0, 0, 0.08)"
          border="1px solid"
          borderColor="rgba(255, 255, 255, 0.3)"
        >
          <Flex align="center" gap={2} mb={2}>
            <Box w={3} h={3} borderRadius="full" bg={data.fill} />
            <Text fontWeight="600" fontSize="sm" color="gray.700">
              {data.name}
            </Text>
          </Flex>
          <Text fontSize="xl" fontWeight="700" color="gray.900">
            {formatarEmReais(data.valor)}
          </Text>
          <Text fontSize="sm" color="gray.500">
            {percentual.toFixed(2)}% do faturamento
          </Text>
        </Box>
      );
    }
    return null;
  };

  // Calcula o teto do gráfico (faturamento + imposto para melhor visualização)
  const maxValorGrafico = taxData.total_faturamento + taxData.vl_simples_nacional;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="5xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <Text fontSize="lg" fontWeight="bold">
            Detalhes Simples Nacional - {taxData.razao_social}
          </Text>
          <Text fontSize="sm" color="gray.500">
            CNPJ: {taxData.cnpj}
          </Text>
          {dateStart && dateEnd && (
            <Text fontSize="sm" color="blue.500" fontWeight="500">
              Periodo: {formatPeriodo(dateStart, dateEnd)}
            </Text>
          )}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {/* Resumo */}
          <SimpleGrid columns={{ base: 1, md: 4 }} spacing={4} mb={6}>
            <Box p={4} bg="blue.50" borderRadius="lg">
              <Text fontSize="sm" color="gray.600">
                Faturamento Total
              </Text>
              <Text fontSize="xl" fontWeight="bold" color="blue.600">
                {formatarEmReais(taxData.total_faturamento)}
              </Text>
            </Box>
            <Box p={4} bg="green.50" borderRadius="lg">
              <Text fontSize="sm" color="gray.600">
                Simples Nacional
              </Text>
              <Text fontSize="xl" fontWeight="bold" color="green.600">
                {formatarEmReais(taxData.vl_simples_nacional)}
              </Text>
            </Box>
            <Box p={4} bg="orange.50" borderRadius="lg">
              <Text fontSize="sm" color="gray.600">
                % sobre Faturamento
              </Text>
              <Text fontSize="xl" fontWeight="bold" color="orange.600">
                {formatarPercentual(taxData.simples_nacional_percentual)}
              </Text>
            </Box>
            <Box p={4} bg="purple.50" borderRadius="lg">
              <Text fontSize="sm" color="gray.600">
                Anexos SN
              </Text>
              <Text fontSize="xl" fontWeight="bold" color="purple.600">
                {companyData.apuracoes?.length ?? 0}
              </Text>
            </Box>
          </SimpleGrid>

          {/* Grafico de Barras e Tabela */}
          <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6} mb={6}>
            {/* Grafico de Barras - Estilo Apple */}
            <Box
              p={6}
              bg="linear-gradient(180deg, rgba(255,255,255,0.95) 0%, rgba(248,250,252,0.95) 100%)"
              borderRadius="16px"
              boxShadow="0 4px 24px rgba(0, 0, 0, 0.06)"
              border="1px solid rgba(0, 0, 0, 0.04)"
            >
              <Text fontSize="md" fontWeight="600" mb={4} color="gray.800">
                Faturamento vs Simples Nacional
              </Text>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={barData} layout="vertical" margin={{ left: 20, right: 30 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
                  <XAxis
                    type="number"
                    domain={[0, maxValorGrafico]}
                    tickFormatter={formatarValorEixoY}
                    tick={{ fontSize: 12, fill: '#6B7280' }}
                  />
                  <YAxis
                    type="category"
                    dataKey="name"
                    width={120}
                    tick={{ fontSize: 12, fill: '#6B7280' }}
                  />
                  <Tooltip content={<CustomBarTooltip />} />
                  <Legend />
                  <Bar
                    dataKey="valor"
                    name="Valor"
                    fill="#8884d8"
                    radius={[0, 8, 8, 0]}
                    animationBegin={0}
                    animationDuration={800}
                  >
                    {barData.map(entry => (
                      <Cell key={`cell-${entry.name}`} fill={entry.fill} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Box>

            {/* Tabela de Detalhes - Estilo Apple */}
            <Box
              p={6}
              bg="linear-gradient(180deg, rgba(255,255,255,0.95) 0%, rgba(248,250,252,0.95) 100%)"
              borderRadius="16px"
              boxShadow="0 4px 24px rgba(0, 0, 0, 0.06)"
              border="1px solid rgba(0, 0, 0, 0.04)"
            >
              <Text fontSize="md" fontWeight="600" mb={4} color="gray.800">
                Detalhamento
              </Text>
              <TableContainer>
                <Table size="sm">
                  <Thead>
                    <Tr>
                      <Th>Descricao</Th>
                      <Th isNumeric>Valor</Th>
                      <Th isNumeric>%</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    <Tr>
                      <Td>
                        <Flex align="center" gap={2}>
                          <Box w={3} h={3} bg={APPLE_COLORS.FATURAMENTO} borderRadius="full" />
                          Faturamento Total
                        </Flex>
                      </Td>
                      <Td isNumeric>{formatarEmReais(taxData.total_faturamento)}</Td>
                      <Td isNumeric>100%</Td>
                    </Tr>
                    <Tr bg="green.50">
                      <Td fontWeight="semibold">
                        <Flex align="center" gap={2}>
                          <Box w={3} h={3} bg={APPLE_COLORS.SIMPLES_NACIONAL} borderRadius="full" />
                          Simples Nacional
                        </Flex>
                      </Td>
                      <Td isNumeric fontWeight="semibold">
                        {formatarEmReais(taxData.vl_simples_nacional)}
                      </Td>
                      <Td isNumeric fontWeight="bold" color="green.600">
                        {formatarPercentual(taxData.simples_nacional_percentual)}
                      </Td>
                    </Tr>
                    <Tr>
                      <Td>Liquido (Faturamento - SN)</Td>
                      <Td isNumeric>
                        {formatarEmReais(taxData.total_faturamento - taxData.vl_simples_nacional)}
                      </Td>
                      <Td isNumeric>
                        {formatarPercentual(100 - taxData.simples_nacional_percentual)}
                      </Td>
                    </Tr>
                  </Tbody>
                </Table>
              </TableContainer>

              {/* Informacoes adicionais do Simples - Estilo Apple */}
              <Box
                mt={6}
                p={4}
                bg="rgba(255, 255, 255, 0.7)"
                borderRadius="12px"
                border="1px solid rgba(0, 0, 0, 0.04)"
              >
                <Flex align="center" gap={2} mb={3}>
                  <Text fontSize="sm" fontWeight="600" color="gray.700">
                    Atividades do Simples Nacional
                  </Text>
                  {companyData.apuracoes && companyData.apuracoes.length > 1 && (
                    <Badge colorScheme="purple" fontSize="xs">
                      {companyData.apuracoes.length} atividades
                    </Badge>
                  )}
                </Flex>
                {companyData.apuracoes && companyData.apuracoes.length > 0 ? (
                  <VStack spacing={2} align="stretch">
                    {companyData.apuracoes.map((atividade, index) => (
                      <Flex
                        key={`modal-atividade-${atividade.anexo}-${atividade.secao ?? 0}-${atividade.tabela ?? 0}-${atividade.valor_guia}`}
                        align="center"
                        justify="space-between"
                        p={2}
                        bg={index % 2 === 0 ? 'gray.50' : 'purple.50'}
                        borderRadius="md"
                        borderLeft="3px solid"
                        borderColor={index % 2 === 0 ? 'orange.400' : 'purple.400'}
                      >
                        <HStack spacing={4}>
                          <Box>
                            <Text fontSize="xs" color="gray.500">
                              Anexo
                            </Text>
                            <Text fontWeight="600" color="gray.800">
                              {atividade.anexo}
                            </Text>
                          </Box>
                          <Box>
                            <Text fontSize="xs" color="gray.500">
                              Seção
                            </Text>
                            <Text fontWeight="600" color="gray.800">
                              {atividade.secao ?? '-'}
                            </Text>
                          </Box>
                          <Box>
                            <Text fontSize="xs" color="gray.500">
                              Tabela
                            </Text>
                            <Text fontWeight="600" color="gray.800">
                              {atividade.tabela ?? '-'}
                            </Text>
                          </Box>
                        </HStack>
                        <Box textAlign="right">
                          <Text fontSize="xs" color="gray.500">
                            Valor Guia
                          </Text>
                          <Text fontWeight="600" color="green.600">
                            {formatarEmReais(atividade.valor_guia)}
                          </Text>
                        </Box>
                      </Flex>
                    ))}
                  </VStack>
                ) : (
                  <Text fontSize="sm" color="gray.400" fontStyle="italic">
                    Sem atividades registradas
                  </Text>
                )}
              </Box>
            </Box>
          </SimpleGrid>

          {/* Tipos de Receita e ISS */}
          <Box
            p={6}
            bg="linear-gradient(180deg, rgba(255,255,255,0.95) 0%, rgba(248,250,252,0.95) 100%)"
            borderRadius="16px"
            boxShadow="0 4px 24px rgba(0, 0, 0, 0.06)"
            border="1px solid rgba(0, 0, 0, 0.04)"
            mb={6}
          >
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
              {/* Tipos de Receita - Lado Esquerdo */}
              <Box>
                <Text fontSize="lg" fontWeight="600" mb={3} color="gray.800">
                  Tipos de Receita
                </Text>
                <VStack spacing={2} align="stretch">
                  <Box
                    position="relative"
                    h="40px"
                    bg="gray.100"
                    borderRadius="md"
                    overflow="hidden"
                  >
                    <Box
                      position="absolute"
                      left={0}
                      top={0}
                      h="100%"
                      w={`${Math.min(calculateTaxPercentage(taxData.vl_servico, taxData.total_faturamento), 100)}%`}
                      bg="blue.400"
                      transition="width 0.5s ease"
                    />
                    <Flex
                      position="absolute"
                      left={0}
                      top={0}
                      w="100%"
                      h="100%"
                      justify="space-between"
                      align="center"
                      px={3}
                      zIndex={1}
                    >
                      <Text fontSize="md" fontWeight="600" color="gray.700">
                        Serviços: {formatarEmReais(taxData.vl_servico)}
                      </Text>
                      <Text fontSize="sm" fontWeight="bold" color="gray.800">
                        {formatarPercentual(
                          calculateTaxPercentage(taxData.vl_servico, taxData.total_faturamento),
                        )}
                      </Text>
                    </Flex>
                    <Text
                      position="absolute"
                      left="50%"
                      top="50%"
                      transform="translate(-50%, -50%)"
                      fontSize="xl"
                      fontWeight="700"
                      color="blue.200"
                      opacity={0.4}
                      zIndex={0}
                      textTransform="uppercase"
                    >
                      FATURAMENTO
                    </Text>
                  </Box>
                  <Box
                    position="relative"
                    h="40px"
                    bg="gray.100"
                    borderRadius="md"
                    overflow="hidden"
                  >
                    <Box
                      position="absolute"
                      left={0}
                      top={0}
                      h="100%"
                      w={`${Math.min(calculateTaxPercentage(taxData.vl_saida, taxData.total_faturamento), 100)}%`}
                      bg="green.400"
                      transition="width 0.5s ease"
                    />
                    <Flex
                      position="absolute"
                      left={0}
                      top={0}
                      w="100%"
                      h="100%"
                      justify="space-between"
                      align="center"
                      px={3}
                      zIndex={1}
                    >
                      <Text fontSize="md" fontWeight="600" color="gray.700">
                        Saídas: {formatarEmReais(taxData.vl_saida)}
                      </Text>
                      <Text fontSize="sm" fontWeight="bold" color="gray.800">
                        {formatarPercentual(
                          calculateTaxPercentage(taxData.vl_saida, taxData.total_faturamento),
                        )}
                      </Text>
                    </Flex>
                    <Text
                      position="absolute"
                      left="50%"
                      top="50%"
                      transform="translate(-50%, -50%)"
                      fontSize="xl"
                      fontWeight="700"
                      color="green.200"
                      opacity={0.4}
                      zIndex={0}
                      textTransform="uppercase"
                    >
                      FATURAMENTO
                    </Text>
                  </Box>
                  <Box
                    position="relative"
                    h="40px"
                    bg="gray.100"
                    borderRadius="md"
                    overflow="hidden"
                  >
                    <Box
                      position="absolute"
                      left={0}
                      top={0}
                      h="100%"
                      w={`${Math.min(calculateTaxPercentage(taxData.vl_outros, taxData.total_faturamento), 100)}%`}
                      bg="purple.400"
                      transition="width 0.5s ease"
                    />
                    <Flex
                      position="absolute"
                      left={0}
                      top={0}
                      w="100%"
                      h="100%"
                      justify="space-between"
                      align="center"
                      px={3}
                      zIndex={1}
                    >
                      <Text fontSize="md" fontWeight="600" color="gray.700">
                        Outros: {formatarEmReais(taxData.vl_outros)}
                      </Text>
                      <Text fontSize="sm" fontWeight="bold" color="gray.800">
                        {formatarPercentual(
                          calculateTaxPercentage(taxData.vl_outros, taxData.total_faturamento),
                        )}
                      </Text>
                    </Flex>
                    <Text
                      position="absolute"
                      left="50%"
                      top="50%"
                      transform="translate(-50%, -50%)"
                      fontSize="xl"
                      fontWeight="700"
                      color="purple.200"
                      opacity={0.4}
                      zIndex={0}
                      textTransform="uppercase"
                    >
                      FATURAMENTO
                    </Text>
                  </Box>
                </VStack>
              </Box>

              {/* ISS - Lado Direito */}
              <Box>
                <Text fontSize="lg" fontWeight="600" mb={3} color="gray.800">
                  ISS
                </Text>
                <VStack spacing={2} align="stretch">
                  <Flex
                    justify="space-between"
                    align="center"
                    p={2}
                    bg="orange.50"
                    borderRadius="md"
                    borderLeft="4px solid"
                    borderColor="orange.400"
                  >
                    <Text fontSize="md" fontWeight="600" color="gray.700">
                      ISS:
                    </Text>
                    <Text fontSize="md" fontWeight="bold" color="orange.600">
                      {formatarEmReais(taxData.iss)}
                    </Text>
                  </Flex>
                  <Flex
                    justify="space-between"
                    align="center"
                    p={2}
                    bg="pink.50"
                    borderRadius="md"
                    borderLeft="4px solid"
                    borderColor="pink.400"
                  >
                    <Text fontSize="md" fontWeight="600" color="gray.700">
                      ISS Retido:
                    </Text>
                    <Text fontSize="md" fontWeight="bold" color="pink.600">
                      {formatarEmReais(taxData.iss_retido)}
                    </Text>
                  </Flex>
                </VStack>
              </Box>
            </SimpleGrid>
          </Box>

          {/* Progressão Faturamento / Simples Nacional - Gráfico mês a mês */}
          <Box
            p={6}
            bg="linear-gradient(180deg, rgba(255,255,255,0.95) 0%, rgba(248,250,252,0.95) 100%)"
            borderRadius="16px"
            boxShadow="0 4px 24px rgba(0, 0, 0, 0.06)"
            border="1px solid rgba(0, 0, 0, 0.04)"
          >
            <VStack align="stretch" spacing={4} mb={4}>
              <Flex justify="space-between" align="center" flexWrap="wrap" gap={3}>
                <Text fontSize="md" fontWeight="600" color="gray.800">
                  Progressão Faturamento / Simples Nacional
                </Text>
                <ButtonGroup size="xs" variant="outline" isAttached>
                  <Button
                    onClick={() => setChartMode('line')}
                    colorScheme={chartMode === 'line' ? 'blue' : 'gray'}
                    variant={chartMode === 'line' ? 'solid' : 'outline'}
                  >
                    Linha
                  </Button>
                  <Button
                    onClick={() => setChartMode('bar')}
                    colorScheme={chartMode === 'bar' ? 'blue' : 'gray'}
                    variant={chartMode === 'bar' ? 'solid' : 'outline'}
                  >
                    Barras
                  </Button>
                </ButtonGroup>
              </Flex>
              <HStack spacing={4} flexWrap="wrap">
                <Checkbox
                  isChecked={showFaturamento}
                  onChange={e => setShowFaturamento(e.target.checked)}
                  colorScheme="blue"
                  size="sm"
                >
                  <HStack spacing={2}>
                    <Box w={3} h={3} borderRadius="full" bg={APPLE_COLORS.FATURAMENTO} />
                    <Text fontSize="sm" color="gray.700">
                      Faturamento
                    </Text>
                  </HStack>
                </Checkbox>
                <Checkbox
                  isChecked={showSimplesNacional}
                  onChange={e => setShowSimplesNacional(e.target.checked)}
                  colorScheme="green"
                  size="sm"
                >
                  <HStack spacing={2}>
                    <Box w={3} h={3} borderRadius="full" bg={APPLE_COLORS.SIMPLES_NACIONAL} />
                    <Text fontSize="sm" color="gray.700">
                      Simples Nacional
                    </Text>
                  </HStack>
                </Checkbox>
              </HStack>
            </VStack>
            {isLoadingSNHistory && (
              <Flex justify="center" align="center" h="300px">
                <Spinner size="lg" color="blue.500" />
              </Flex>
            )}
            {!isLoadingSNHistory && snHistoryData && snHistoryData.length > 0 && (
              <ResponsiveContainer width="100%" height={300}>
                {chartMode === 'line' ? (
                  <LineChart data={snHistoryData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
                    <XAxis dataKey="mes" tick={{ fontSize: 12, fill: '#6B7280' }} />
                    <YAxis
                      tickFormatter={value => formatarValorEixoY(value)}
                      tick={{ fontSize: 12, fill: '#6B7280' }}
                    />
                    <Tooltip
                      content={({ active, payload, label }) => {
                        if (active && payload && payload.length) {
                          const data = payload[0].payload as ITaxAnalysisSNHistory;
                          return (
                            <Box
                              bg="rgba(255, 255, 255, 0.85)"
                              backdropFilter="blur(12px)"
                              p={4}
                              borderRadius="12px"
                              boxShadow="0 4px 24px rgba(0, 0, 0, 0.08)"
                              border="1px solid"
                              borderColor="rgba(255, 255, 255, 0.3)"
                            >
                              <Text fontWeight="700" mb={2} color="gray.800" fontSize="sm">
                                {label}
                              </Text>
                              <Flex direction="column" gap={1}>
                                <Flex justify="space-between" gap={4}>
                                  <Text fontSize="xs" color="gray.600">
                                    Faturamento:
                                  </Text>
                                  <Text fontSize="xs" fontWeight="600" color="gray.800">
                                    {formatarEmReais(data.total_faturamento)}
                                  </Text>
                                </Flex>
                                <Flex justify="space-between" gap={4}>
                                  <Text fontSize="xs" color="gray.600">
                                    Simples Nacional:
                                  </Text>
                                  <Text fontSize="xs" fontWeight="600" color="green.600">
                                    {formatarEmReais(data.vl_simples_nacional)}
                                  </Text>
                                </Flex>
                                <Flex justify="space-between" gap={4}>
                                  <Text fontSize="xs" color="gray.600">
                                    % SN/Faturamento:
                                  </Text>
                                  <Text fontSize="xs" fontWeight="600" color="gray.800">
                                    {formatarPercentual(data.simples_nacional_percentual)}
                                  </Text>
                                </Flex>
                              </Flex>
                            </Box>
                          );
                        }
                        return null;
                      }}
                    />
                    <Legend />
                    {showFaturamento && (
                      <Line
                        type="monotone"
                        dataKey="total_faturamento"
                        name="Faturamento"
                        stroke={APPLE_COLORS.FATURAMENTO}
                        strokeWidth={2.5}
                        dot={{
                          fill: '#fff',
                          strokeWidth: 2,
                          r: 4,
                          stroke: APPLE_COLORS.FATURAMENTO,
                        }}
                        activeDot={{
                          r: 6,
                          strokeWidth: 2,
                          stroke: APPLE_COLORS.FATURAMENTO,
                          fill: '#fff',
                        }}
                        animationBegin={0}
                        animationDuration={800}
                      />
                    )}
                    {showSimplesNacional && (
                      <Line
                        type="monotone"
                        dataKey="vl_simples_nacional"
                        name="Simples Nacional"
                        stroke={APPLE_COLORS.SIMPLES_NACIONAL}
                        strokeWidth={2.5}
                        dot={{
                          fill: '#fff',
                          strokeWidth: 2,
                          r: 4,
                          stroke: APPLE_COLORS.SIMPLES_NACIONAL,
                        }}
                        activeDot={{
                          r: 6,
                          strokeWidth: 2,
                          stroke: APPLE_COLORS.SIMPLES_NACIONAL,
                          fill: '#fff',
                        }}
                        animationBegin={0}
                        animationDuration={800}
                      />
                    )}
                  </LineChart>
                ) : (
                  <BarChart data={snHistoryData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
                    <XAxis dataKey="mes" tick={{ fontSize: 12, fill: '#6B7280' }} />
                    <YAxis
                      tickFormatter={value => formatarValorEixoY(value)}
                      tick={{ fontSize: 12, fill: '#6B7280' }}
                    />
                    <Tooltip
                      content={({ active, payload, label }) => {
                        if (active && payload && payload.length) {
                          const data = payload[0].payload as ITaxAnalysisSNHistory;
                          return (
                            <Box
                              bg="rgba(255, 255, 255, 0.85)"
                              backdropFilter="blur(12px)"
                              p={4}
                              borderRadius="12px"
                              boxShadow="0 4px 24px rgba(0, 0, 0, 0.08)"
                              border="1px solid"
                              borderColor="rgba(255, 255, 255, 0.3)"
                            >
                              <Text fontWeight="700" mb={2} color="gray.800" fontSize="sm">
                                {label}
                              </Text>
                              <Flex direction="column" gap={1}>
                                <Flex justify="space-between" gap={4}>
                                  <Text fontSize="xs" color="gray.600">
                                    Faturamento:
                                  </Text>
                                  <Text fontSize="xs" fontWeight="600" color="gray.800">
                                    {formatarEmReais(data.total_faturamento)}
                                  </Text>
                                </Flex>
                                <Flex justify="space-between" gap={4}>
                                  <Text fontSize="xs" color="gray.600">
                                    Simples Nacional:
                                  </Text>
                                  <Text fontSize="xs" fontWeight="600" color="green.600">
                                    {formatarEmReais(data.vl_simples_nacional)}
                                  </Text>
                                </Flex>
                                <Flex justify="space-between" gap={4}>
                                  <Text fontSize="xs" color="gray.600">
                                    % SN/Faturamento:
                                  </Text>
                                  <Text fontSize="xs" fontWeight="600" color="gray.800">
                                    {formatarPercentual(data.simples_nacional_percentual)}
                                  </Text>
                                </Flex>
                              </Flex>
                            </Box>
                          );
                        }
                        return null;
                      }}
                    />
                    <Legend />
                    {showFaturamento && (
                      <Bar
                        dataKey="total_faturamento"
                        name="Faturamento"
                        fill={APPLE_COLORS.FATURAMENTO}
                        radius={[8, 8, 0, 0]}
                        animationBegin={0}
                        animationDuration={800}
                      />
                    )}
                    {showSimplesNacional && (
                      <Bar
                        dataKey="vl_simples_nacional"
                        name="Simples Nacional"
                        fill={APPLE_COLORS.SIMPLES_NACIONAL}
                        radius={[8, 8, 0, 0]}
                        animationBegin={0}
                        animationDuration={800}
                      />
                    )}
                  </BarChart>
                )}
              </ResponsiveContainer>
            )}
            {!isLoadingSNHistory && (!snHistoryData || snHistoryData.length === 0) && (
              <Flex justify="center" align="center" h="300px">
                <Text color="gray.500">Sem dados históricos disponíveis</Text>
              </Flex>
            )}
          </Box>
        </ModalBody>
        <ModalFooter>
          <Button colorScheme="blue" onClick={onClose}>
            Fechar
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
